import React, { ReactNode, useMemo } from "react";
import { renderToString } from "react-dom/server";
import Highcharts, { Point, PointClickEventObject } from "highcharts";
import { useTheme } from "@mui/material";
import { toCurrency } from "@helpers";
import { getCommonHighchartsOptions } from "../PerformanceCharts/commonChartOptions";
import { IconValueTag } from "./IconValueTag";
import { TooltipTable } from "./TooltipTable";
import { DonutChartDataItem } from "./DonutChartWithTotal";
import { TooltipPercentage } from "./TooltipPercentage";

const CHART_TAG_LOWEST_PERCENTAGE = 3;

type PointObject = Point & { id: number; icon: ReactNode };

interface Props {
  chartData: DonutChartDataItem[] | undefined;
  selectedId: string | null;
  height: number;
  width: number;
  isFetching: boolean;
  onSectionClick?: (id: string) => void;
  tooltipType?: "percentage" | "table";
  showLabels?: boolean;
  disabled?: boolean;
  centerDetails?: {
    title?: string;
    value?: number;
    innerSize?: string;
    titleSize?: string;
    valueSize?: string;
  };
  setHoveredId?: (id?: string) => void;
}

export const useChartOptions = ({
  chartData,
  selectedId,
  height,
  width,
  isFetching,
  onSectionClick,
  showLabels,
  disabled,
  centerDetails,
  tooltipType,
  setHoveredId,
}: Props): Highcharts.Options => {
  const theme = useTheme();
  const [tooltipExpandedIds, setTooltipExpandedIds] = React.useState<number[]>(
    []
  );
  const commonHighchartsOptions = useMemo(
    () => getCommonHighchartsOptions(theme),
    [theme]
  );

  const chartColors = useMemo(
    () => chartData?.map((x) => x.color).filter((x) => x !== undefined) ?? [],
    [chartData]
  );

  const data = useMemo(
    () =>
      chartData
        ?.map((x) => ({ ...x, sliced: selectedId === x.id }))
        .filter((x) => !x.hiddenInChart),
    [chartData, selectedId]
  );

  const hasNoData = chartData?.length === 1 && chartData[0].value === 0;

  return useMemo(() => {
    return {
      title: { text: "" },
      subtitle: {
        align: "center",
        verticalAlign: "middle",
        useHTML: true,
        text: !isFetching
          ? renderToString(
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: centerDetails?.valueSize ?? "18px",
                    lineHeight: "24px",
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {centerDetails?.value
                    ? toCurrency(centerDetails?.value, "EUR", {
                        minimumFractionDigits: 2,
                      })
                    : "-"}
                </div>
                <div
                  style={{
                    fontSize: centerDetails?.titleSize ?? "14px",
                    lineHeight: "22px",
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {centerDetails?.title}
                </div>
              </div>
            )
          : "",
      },
      credits: { enabled: false },
      tooltip: {
        ...commonHighchartsOptions.tooltip,
        enabled: !disabled,
        followPointer: false,
        useHTML: true,
        formatter: function () {
          if (tooltipType === "table") {
            return renderToString(
              <TooltipTable
                // @ts-ignore
                point={this.point}
                theme={theme}
                totalMessage={`${centerDetails?.title}:`}
                expandedIds={tooltipExpandedIds}
              />
            );
          }

          return renderToString(
            <TooltipPercentage
              // @ts-ignore
              point={this.point}
              theme={theme}
            />
          );
        },
      },
      chart: {
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        plotShadow: false,
        type: "pie",
        height,
        width,
        spacing: [0, 0, 0, 0],
        events: {
          load: function () {
            const chart = this;

            const originalRefresh = chart.tooltip.refresh;

            chart.tooltip.refresh = function (point, mouseEvent) {
              originalRefresh.apply(this, [point, mouseEvent]);

              const expandTextNodes = document.getElementsByClassName(
                "tooltip-table-text-expand"
              );

              if (expandTextNodes?.length > 0) {
                Array.from(expandTextNodes).forEach((node) => {
                  const textNode = node as HTMLElement;
                  textNode.onclick = function (e: any) {
                    setTooltipExpandedIds((prevState) => [
                      ...prevState,
                      +(e?.target as HTMLElement)?.id,
                    ]);
                  };
                });
              }
            };
          },
        },
      },
      colors: chartColors as string[],
      plotOptions: {
        pie: {
          enableMouseTracking: !disabled,
          allowPointSelect: !hasNoData && !disabled,
          cursor: disabled ? "default" : "pointer",
          borderWidth: isFetching ? 2 : 0,
          borderRadius: 0,
          size: "100%",
          innerSize: centerDetails?.innerSize ?? "55%",

          dataLabels: {
            enabled: showLabels,
            useHTML: true,
            distance: -30,
            style: {
              color: theme.palette.text.primary,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body1.fontSize as string,
              lineHeight: theme.typography.body1.lineHeight,
              fontWeight: "600",
            },
            formatter: function () {
              const point = this.point as PointObject;
              const percentage = point.percentage;
              const icon = point.icon;
              const name = point.name;

              if (
                !name ||
                !percentage ||
                percentage < CHART_TAG_LOWEST_PERCENTAGE ||
                hasNoData
              )
                return null;

              return renderToString(
                <IconValueTag percent={percentage} theme={theme} icon={icon} />
              );
            },
          },
          point: {
            events: {
              click: function (event: PointClickEventObject) {
                if (hasNoData || !this.series.chart.series[0].finishedAnimating)
                  return;
                const point = event.point as PointObject;
                onSectionClick?.(point.id.toString());
              },
              mouseOver: function (event: any) {
                if (!this.series.chart.series[0].finishedAnimating) return;
                setHoveredId?.(event.target?.id?.toString());
              },
              mouseOut: function () {
                if (!this.series.chart.series[0].finishedAnimating) return;
                setHoveredId?.(undefined);
              },
            },
          },
          states: {
            hover: {
              enabled: !disabled,
              brightness: 0.1,
            },
            inactive: {
              enabled: false,
            },
          },
        },
      },
      series: [
        {
          colorByPoint: true,
          data: data ?? [],
          type: "pie",
        },
      ],
    };
  }, [
    commonHighchartsOptions,
    hasNoData,
    height,
    width,
    theme,
    onSectionClick,
    chartColors,
    data,
    tooltipExpandedIds,
    isFetching,
    showLabels,
    disabled,
    centerDetails,
    tooltipType,
    setHoveredId,
  ]);
};
